/* eslint-disable no-param-reassign */
import { cloneDeep } from 'lodash';
import CustomPolicyGroupTemplatesApi from 'client/api/v2/CustomPolicyGroupTemplatesApi';
import ProvidersApi from 'client/api/v2/ProvidersApi';
import createModel from 'client/store/modules/models/createModel';

export const getDefaultItem = () => ({
  description: '',
  name: '',
});

export const getDefaultState = () => ({
  boundPolicies: [],
  hasBoundItemsApiFetchBeenCalled: false,
  limit: 10,
  originalBoundPolicies: [],
  policiesToBind: [],
  policiesToConvert: [],
  policiesToUnbind: [],
  searchTerm: '',
  skip: 0,
  sortField: 'name',
  totalCount: 0,
});

const model = createModel(getDefaultItem, 'id', CustomPolicyGroupTemplatesApi);

const allProgress = (proms, progressCb) => {
  let d = 0;
  progressCb(0);
  proms.forEach(p => {
    p.then(() => {
      d += 1;
      progressCb(d);
    });
  });
  return Promise.all(proms);
};

export default {
  namespaced: true,

  state: {
    ...model.state,
    ...getDefaultState(),
  },

  actions: {
    ...model.actions,

    fetchItem(context = { getters: { id: {} } }) {
      const { id } = context.getters;
      const providerId = context.rootGetters['SettingsModule/getUser'].provider;
      CustomPolicyGroupTemplatesApi.setProviderId(providerId);

      if (id) {
        return CustomPolicyGroupTemplatesApi
          .get(id)
          .then((item) => {
            // Clone the item so that any changes to non-primitive type values,
            // such as an array of phone numbers, are _not_ accidentally made
            // to the previous item when the item is mutated.

            context.commit('SET_ITEM', cloneDeep(item));
            context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
          });
      }

      return Promise.resolve();
    },

    fetchBoundPolicies({ commit, state }, id) {
      const {
        skip, limit, searchTerm, sortField,
      } = state;

      if (!state.hasBoundItemsApiFetchBeenCalled) {
        const fetchAllArgs = { skip: 0, limit: 100, sort: 'name' };
        CustomPolicyGroupTemplatesApi.fetchBoundPolicies(id, fetchAllArgs).then((response) => {
          commit('SET_ORIGINAL_BOUND_POLICIES', response.results);
        });
      }

      const args = {
        skip,
        limit,
        searchTerm,
        sort: sortField,
      };

      return CustomPolicyGroupTemplatesApi.fetchBoundPolicies(id, args)
        .then((response) => {
          commit('SET_TOTAL_BOUND_POLICY_COUNT', response.totalCount);
          commit('SET_BOUND_POLICIES', response.results);
        });
    },

    async convertPoliciestoConfigureTemplates(context) {
      const createTasks = [];
      const { policiesToConvert } = context.state;
      const providerId = context.rootGetters['SettingsModule/getUser'].provider;

      const addPolicyIds = policiesToConvert.map(x => x.id);

      addPolicyIds.forEach(
        newId => {
          createTasks.push(
            ProvidersApi.convertPolicies(
              providerId,
              newId,
            ),
          );
        },
      );

      return Promise.all(createTasks);
    },

    async saveMembers(context = { getters: { id: {} } }, progressCb) {
      const { id } = context.getters;
      const { policiesToBind, policiesToUnbind } = context.state;

      const removePolicyIds = policiesToUnbind.map(x => x.id);
      const addPolicyIds = policiesToBind.map(x => x.id);

      const actions = [];

      // Remove any policies to unbind
      removePolicyIds.forEach((memberId) => {
        actions.push(CustomPolicyGroupTemplatesApi.removeMember(id, memberId));
      });

      // Add any policies to bind
      addPolicyIds.forEach((memberId) => {
        actions.push(CustomPolicyGroupTemplatesApi.addMember(id, memberId));
      });

      // Perform actions and report progress if present
      const progressFunc = (i) => {
        if (progressCb) progressCb(i, actions.length);
      };

      await allProgress(actions, progressFunc);
    },

    async saveItem(context) {
      const { getters = {} } = context;

      const handleSaveItem = (item) => {
        context.commit('SET_ITEM', cloneDeep(item));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(item));
        context.commit('SET_ITEM_STATE', { update: true });
        return item;
      };

      await CustomPolicyGroupTemplatesApi.update(getters.item).then(handleSaveItem);

      await context.dispatch('fetchItem');
    },

    async setSortField({ commit }, { sortField }) {
      commit('SET_SORT_FIELD', sortField);
    },

    setHasBoundItemsApiFetchBeenCalled({ commit }) {
      commit('SET_API_FETCH_HAS_BEEN_CALLED');
    },

    setItemField(context, { key, value }) {
      model.actions.setItemField(context, { key, value });
    },

    setPage({ commit }, { pageNumber, limit }) {
      commit('SET_SKIP', pageNumber * limit);
      commit('SET_LIMIT', limit);
    },

    setSearchTerm({ commit }, term) {
      commit('SET_SEARCH_TERM', term);
    },

    setSelectedBoundPolicyItem({ commit }, boundPolicyId, selected) {
      commit('SET_SELECTED_STATE_BOUND_POLICY_ITEM', {
        id: boundPolicyId,
        selected,
      });
    },

    setAddedBoundPolicyItem({ commit }, boundPolicyId) {
      commit('SET_POLICY_TO_CONVERT', {
        id: boundPolicyId,
      });
    },

    setAllBoundPolicies({ commit }, ids) {
      ids.forEach((id) => {
        commit('SET_POLICIES_TO_CONVERT', {
          id,
        });
      });
    },

    addConvertedBoundPolicies({ commit }, boundPolicyIds) {
      boundPolicyIds.forEach((id) => {
        commit('SET_POLICIES_TO_BIND', {
          id,
        });
      });
    },

    clearPoliciesToAdd({ commit }) {
      commit('UNSET_POLICIES_TO_ADD');
    },

    clearPolicies({ commit }) {
      commit('UNSET_POLICIES');
    },

    unsetAddedBoundPolicyItem({ commit }, boundPolicyId) {
      commit('UNSET_POLICY_TO_CONVERT', {
        id: boundPolicyId,
      });
    },

    toggleSelectedBoundPolicyItem({ dispatch }, boundPolicyId) {
      dispatch('setSelectedBoundPolicyItem', boundPolicyId);
    },

    toggleSelectedBoundPolicies({ commit }, selectedState) {
      // Will toggle all bound policies selected state
      commit('SET_SELECTED_STATE_BOUND_POLICIES', selectedState);
    },
  },

  getters: {
    ...model.getters,

    getBoundPolicies(state) {
      return state.boundPolicies;
    },

    getSearchTerm(state) {
      return state.searchTerm;
    },

  },

  mutations: {
    ...model.mutations,

    RESET_ITEM(state = { itemState: {} }) {
      Object.assign(state, getDefaultState());

      // state from parent model
      state.item = getDefaultItem();
      state.itemState.create = undefined;
      state.itemState.update = undefined;
      state.previousItem = getDefaultItem();
    },

    SET_API_FETCH_HAS_BEEN_CALLED(state) {
      state.hasBoundItemsApiFetchBeenCalled = false;
    },

    SET_BOUND_POLICIES(state, boundPolicies) {
      // Loop over each policy and check if we have a tracked selected state,
      // uncheck as needed
      const unbindIds = state.policiesToUnbind.map(x => x.id);
      if (unbindIds.length) {
        boundPolicies.forEach((x) => {
          if (unbindIds.includes(x.id)) {
            x.selected = false;
          }
        });
      }

      state.boundPolicies = [...boundPolicies];
    },

    SET_LIMIT(state, limit) {
      state.limit = limit;
    },

    SET_ORIGINAL_BOUND_POLICIES(state, boundPolicies) {
      state.hasBoundItemsApiFetchBeenCalled = true;
      state.originalBoundPolicies = boundPolicies;
    },

    SET_SEARCH_TERM(state, term) {
      state.searchTerm = term;
    },

    SET_SELECTED_STATE_BOUND_POLICIES(state, selectedState) {
      // if select all, clear toBind, clear toUnbind, set to original
      // if deselect all, clear toBind, set toUnbind, change all to deselect

      if (selectedState) {
        state.policiesToBind = [];
        state.policiesToUnbind = [];
        state.boundPolicies = [...state.originalBoundPolicies];
      }

      if (!selectedState) {
        state.policiesToBind = [];
        state.policiesToUnbind = [...state.originalBoundPolicies];
        state.boundPolicies = state.boundPolicies.map(x => ({
          ...x,
          selected: false,
        }));
      }
    },

    SET_POLICY_TO_CONVERT(state, id) {
      // If the item is not already bound (at DB), add to binding list
      const bindIndex = state.policiesToConvert.findIndex(x => x.id === id);
      if (bindIndex === -1) {
        state.policiesToConvert.push(id);
      }
    },

    SET_POLICIES_TO_CONVERT(state, id) {
      state.policiesToConvert.push(id);
    },

    UNSET_POLICIES(state) {
      state.policiesToConvert = [];
      state.policiesToBind = [];
      state.policiesToUnbind = [];
    },

    UNSET_POLICIES_TO_ADD(state) {
      state.policiesToConvert = [];
      state.policiesToBind = [];
    },

    SET_POLICIES_TO_BIND(state, id) {
      state.policiesToBind.push(id);
    },

    UNSET_POLICY_TO_CONVERT(state, id) {
      const bindIndex = state.policiesToConvert.findIndex(x => x.id === id);

      state.policiesToConvert.splice(bindIndex, 1);
    },

    SET_SELECTED_STATE_BOUND_POLICY_ITEM(state, obj) {
      const { id, selected = undefined } = obj;

      // Find the item by id from bound policies
      const itemIndex = state.boundPolicies.findIndex(p => p.id === id);
      const item = state.boundPolicies[itemIndex];

      // Toggle (inverse) the selected state by default
      let newSelectedState = !item.selected;

      // Unless a specific state is provided, then set it to that
      if (selected !== undefined) {
        newSelectedState = selected;
      }

      state.boundPolicies.splice(itemIndex, 1, {
        ...item,
        selected: newSelectedState,
      });

      // If the item is now checked, remove it from unbind, add to bind
      // If the item is now unchecked, remove it from bind, add to unbind
      const bindIndex = state.policiesToBind.findIndex(x => x.id === id);
      const unbindIndex = state.policiesToUnbind.findIndex(x => x.id === id);
      const originalIndex = state.originalBoundPolicies.findIndex(x => x.id === id);

      if (newSelectedState) {
        if (unbindIndex > -1) {
          state.policiesToUnbind.splice(unbindIndex, 1);
        }

        // If the item is not already bound (at DB), add to binding list
        if (originalIndex === -1) {
          state.policiesToBind.push(item);
        }
      }

      if (!newSelectedState) {
        if (bindIndex > -1) {
          state.policiesToBind.splice(bindIndex, 1);
        }

        if (originalIndex > -1) {
          state.policiesToUnbind.push(item);
        }
      }
    },

    SET_SKIP(state, skip) {
      state.skip = skip;
    },

    SET_SORT_FIELD(state, newSortField) {
      state.sortField = newSortField;
    },

    SET_TOTAL_BOUND_POLICY_COUNT(state, totalCount) {
      state.totalCount = totalCount;
    },
  },
};
